import { ActionButton, CommandBar, DetailsList, DetailsListLayoutMode, HoverCard, HoverCardType, IColumn, ICommandBarItemProps, Label, Link, MessageBar, MessageBarType, ProgressIndicator, SearchBox, SelectionMode, ShimmeredDetailsList, Spinner, Stack, Toggle } from '@fluentui/react';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { getHiddenCouresworksForAccreditor } from '../data/coursework';
import { getItem, getItems } from '../data/sql';
import { getServerHost, isCanvasStudent, isCanvasTeacher, isCurrentUserAStudentOrTeacherInCourse, isKeplerAccreditor, sendApiRequest, styles, thisIsDevEnvironment } from '../lib/helper';
import { useAccreditationContext } from '../providers/accreditationprovider';
import { IAccreditationRulesContext, useAccreditationRulesContext } from '../providers/accreditationrulesprovider';
import { useAssignmentContext } from '../providers/assignmentsprovider';
import { useAuthenticationContext } from '../providers/authenticationprovider';
import { CourseWorkProvider, CourseWorksContext } from '../providers/courseworkprovider';
import { HelperService } from '../services/helperService';
import { IAssignment, ICourseWorkFlat } from '../types/IAssignment';
import { IAssignmentTag, IPossibleTag } from '../types/IAssignmentTags';
import { IAuthenticationStatusContext } from '../types/IAuthenticationStatusContext';
import { ICourse, ISemester } from '../types/ICourse';
import { IApiContext } from '../types/Shared';
import CourseWorkListView from './courseworkListView';
import { FileUploadControl } from './FileUploadControl';
import GalleryView from "./galleryview";
import TagConfigurationPanel from './tagConfigurationPanel';
import { debounce } from '@microsoft/sp-lodash-subset';

export const VIEW_KEY = "KEPLER_COURSE_WORK_LIST_VIEW_KEY";

export const noAssignmentsComponentString = (accreditorStudentsAssigned?: string[], hiddenToggleEnabled?: boolean) => {
    let retVal = "There are no files for this assignment";
    if(hiddenToggleEnabled)
    {
        retVal = "There are no hidden files for this assignment. Try disabling the 'Show Hidden Files' toggle to see files.";
    }
    if (accreditorStudentsAssigned && accreditorStudentsAssigned.length === 0) {
        retVal = "There are no files for this assignment or your accreditor has not been assigned any students for this course";
    }
    return retVal;
}

export interface IListViewCourseworksProperties {
    files: ICourseWorkFlat[];
    studentsAssignedToAccreditor?: string[];
    allowedTags?: IPossibleTag[];
    assignmentTagMapping?: IAssignmentTag;
    currentStudent?: string;
    selectedMyCoursework?: boolean;
    authContext?: IAuthenticationStatusContext;
    accreditationRulesContext?: IAccreditationRulesContext;
    accreditorHiddenCourseWorks?: string[];
    shouldGetDataFromCanvasAPIs: boolean;
    semseter: ISemester;
    assignment: IAssignment;
    studentsInCourse: string[];
    showHiddenCourseWorks: boolean;
    maxFileSizeBeforeStream: number;
}

const fakeCourseWork: any = {
    FileName: "",
    FileSize: -1,
    PictureSize: ""
}

const max = 10;
let fakeCourseWorks: any[] = [];
for (let i = 0; i < max; i++) {
    fakeCourseWorks.push(fakeCourseWork);
}


let shimmerColumns: IColumn[] = [
    {
        key: "name",
        name: "Name",
        minWidth: 500,
    },
    {
        key: "pictureSize",
        name: "Picture Size",
        isResizable: true,
        minWidth: 200,
    },
    {
        key: "fileSize",
        name: "File Size",
        isResizable: true,
        minWidth: 100,
    }
];

const Coursework: React.FunctionComponent<{ shouldGetDataFromCanvasAPIs: boolean, students?: string[], course: ICourse }> = ({ students, shouldGetDataFromCanvasAPIs, course }) => {
    const authContext = useAuthenticationContext();
    const accreditorRulesContext = useAccreditationRulesContext();
    const accreditorContext = useAccreditationContext();
    const isGalleryView = (!localStorage.getItem(VIEW_KEY) && authContext.Result && isKeplerAccreditor(authContext.Result)) ? true : localStorage.getItem(VIEW_KEY) === "GalleryView";
    const assignmentContext = useAssignmentContext();
    const [selectedMyCoursework, setSelectedMyCoursework] = useState(false);
    const [galleryView, setGalleryView] = useState(isGalleryView);
    const [isFileUploadDialogOpen, setIsFileDialogUploadOpen] = useState(false);
    const [isTagConfigurationPanelOpen, setIsTagConfigurationPanelOpen] = useState(false);
    const [assignmentTagMapping, setAssignmentTagMapping] = React.useState<IAssignmentTag | undefined>();
    const [allowedTags, setAllowedTags] = React.useState<IPossibleTag[] | undefined>();
    const [accreditorHiddenCourseWorks, setAccreditorHiddenCourseWorks] = React.useState<string[]>();
    const [error, setError] = useState();
    const [searchText, setSearchText] = useState<string | undefined>();
    const [resetFlag, setResetFlag] = React.useState(false);
    const [landingPageImage, setLandingPageImage] = React.useState<string>();
    const [uploadingLandingPage, setUploadingLandingPage] = React.useState<boolean>(false);
    const [showHiddenFiles, setShowHiddenFiles] = React.useState<boolean>(false);
    const backgroundImageInputRef = useRef<HTMLInputElement>(null);
    const [maxFileSizeBeforeStream, setMaxFileSizeBeforeStream] = React.useState<number>(50)

    HelperService.getKeplerEnvironmentVariable("REACT_APP_MAX_FILE_SIZE_BEFORE_STREAM_IN_MB").then((res: string | undefined) => {
        setMaxFileSizeBeforeStream(res ? Number(res) : 50)
    });

    useEffect(() => {
        if (assignmentContext.SelectedAssignment) {
            setAssignmentTagMapping(undefined);
            getItem("getAssignmentTagMapping", "AssignmentPath", assignmentContext.SelectedAssignment.Path).then(assignmentTagMapping => {
                if (assignmentTagMapping?.TagType) {
                    setAssignmentTagMapping(assignmentTagMapping);
                }
            }).catch(err => {
                setError(err)
            });
            getItems("getAllowedTagsForAssignment", `AssignmentPath=${assignmentContext.SelectedAssignment.Path}`).then(tags => {
                setAllowedTags(tags);
            })
        }
        else {
            const imagePage = `${course.BlobFolderName}/course_landing.png`;
            const url = `/api/blobStorage/getBlobAsBase64FromPath?path=${encodeURIComponent(imagePage)}`;
            sendApiRequest(url, "GET").then(res => {
                setLandingPageImage(res);
            }).catch(err => {
                console.log("No landing page image");
            });
        }
        if (authContext.Result && isKeplerAccreditor(authContext.Result) && accreditorContext.Result?.AccreditorId) {
            getHiddenCouresworksForAccreditor().then(hiddenCourseworks => {
                setAccreditorHiddenCourseWorks(hiddenCourseworks);
            }).catch(err => {
                setError(err);
            });
        }
        setSearchText(undefined);
    }, [assignmentContext.SelectedAssignment, authContext.Result, accreditorContext.Result])

    const currentUserIsStudentInCourse = assignmentContext.SelectedAssignment && authContext.Result && isCanvasStudent(authContext.Result) && isCurrentUserAStudentOrTeacherInCourse(authContext.Result!, assignmentContext.SelectedAssignment.Course);
    const currentUserIsStudentInCourseAndCourseIsActive = currentUserIsStudentInCourse && assignmentContext.SelectedAssignment?.Course?.IsCurrentlyActive;
    const currentUseIsTeacherInCourse = authContext.Result && isCanvasTeacher(authContext.Result) && isCurrentUserAStudentOrTeacherInCourse(authContext.Result!, course);
    const currentUserIsTeacherInCourseAndCourseIsActive = currentUseIsTeacherInCourse && course.IsCurrentlyActive;

    const _getCommandBarItems = (files: ICourseWorkFlat[]): ICommandBarItemProps[] => {
        let retVal: ICommandBarItemProps[] = []
        retVal.push({
            key: "search",
            renderedInOverflow: true,
            onRender: () => {
                return <div style={{ marginLeft: 10, display: "flex", alignItems: "center" }}>             
                    <SearchBox
                        styles={{ root: { width: 400 } }}
                        underlined
                        disableAnimation
                        defaultValue={searchText}
                        onChange={debounce((_: ChangeEvent<HTMLInputElement> | undefined, newValue?: string | undefined) => setSearchText(newValue || ''), 350)}
                        onClear={() => { setSearchText(""); }}
                        placeholder={`Search files`} />          
                </div>;
            }
        })
        if (currentUserIsTeacherInCourseAndCourseIsActive) {
            retVal.push({
                key: "configureAssignmentTags",
                text: "Configure Tags",
                iconProps: {
                    iconName: "Tag"
                },
                onClick: () => {
                    setIsTagConfigurationPanelOpen(true)
                }
            });
        }
        if (students && students.length > 0 && authContext.Result && isCanvasTeacher(authContext.Result)) {
            const studentsWhoHaveUploadedAtLeastOneFile = files?.map(f => f.Student).filter((v, i, a) => a.indexOf(v) === i).filter(s => students.includes(s));

            let studentsArray: any[] = [];
            students.forEach(student => {
                const totalFilesForStudent = files?.map(f => f.Student).filter(s => s.toLowerCase() === student.toLowerCase() && students.includes(s)).length || 0;
                studentsArray.push({
                    "Student": student,
                    "UploadedFiles": totalFilesForStudent
                })
            })
            retVal.push({
                key: "students",
                commandBarButtonAs: () => {
                    // return <Dropzone />;
                    return <HoverCard cardOpenDelay={500} type={HoverCardType.plain} plainCardProps={{
                        onRenderPlainCard: () => {
                            return authContext.Result?.Courses && authContext.Result.Courses.length > 0 ? <div style={{ paddingBottom: 10, paddingLeft: 10, paddingRight: 10 }}>
                                <DetailsList selectionMode={SelectionMode.none} items={studentsArray} columns={[
                                    { key: "Student", name: "Student", fieldName: "Student", minWidth: 150 }, { key: "UploadedFiles", name: "Uploaded Files", fieldName: "UploadedFiles", minWidth: 110 }
                                ]} />
                            </div> : <>
                                { }
                            </>
                        }
                    }}><div style={{ lineHeight: "44px", marginLeft: 20 }}>
                            {studentsWhoHaveUploadedAtLeastOneFile.length} / {students.length} Students
                        </div>
                    </HoverCard>;
                }
            });
        }
        if (currentUserIsStudentInCourseAndCourseIsActive || currentUserIsTeacherInCourseAndCourseIsActive) {
            retVal.push({
                key: "upload1",
                text: "Upload",
                commandBarButtonAs: () => {
                    return <ActionButton iconProps={{ iconName: "Upload" }} onClick={() => {
                        setIsFileDialogUploadOpen(true);
                        setTimeout(() => {
                            setIsFileDialogUploadOpen(false);
                        }, 100)
                    }} text="Upload" />
                }
            });
        }
        if (currentUseIsTeacherInCourse || currentUserIsStudentInCourse || authContext?.Result?.KeplerAuthStatus?.isKeplerAdministrator) {
            retVal.push({
                key: "showHiddenFiles",
                text: "showHiddenFiles",
                commandBarButtonAs: () => {
                    return <div style={{ lineHeight: "44px", marginLeft: 20 }}>
                        <Stack horizontal verticalAlign="center" tokens={{childrenGap: 8}}>
                        <span>Show Hidden Files</span>
                        <Toggle checked={showHiddenFiles} onChange={() => {
                            setShowHiddenFiles(!showHiddenFiles);
                            setResetFlag(!resetFlag);
                        }} />
                    </Stack>
                    </div>
                }
            });
        }
        return retVal;
    };

    const _getCommandBarFarItems = (): ICommandBarItemProps[] => {
        let retVal = [];

        if (currentUserIsStudentInCourse) {
            retVal.push({
                key: "myCourseWork",
                text: "My Course Work",
                iconProps: { iconName: "CalendarWorkWeek" },
                buttonStyles: { root: styles.commandBarButtonRoot, rootDisabled: styles.commandBarButtonRoot, rootChecked: styles.commandBarButtonRootChecked, iconChecked: styles.commandBarButtonRootChecked },
                checked: selectedMyCoursework,
                onClick: () => {
                    setSelectedMyCoursework(!selectedMyCoursework);
                }
            });
        }
        retVal.push(
            {
                key: "galleryView",
                text: `${galleryView ? "List View" : "Gallery View"}`,
                iconProps: { iconName: `${galleryView ? "GroupedList" : "ImageCrosshair"}` },
                buttonStyles: { root: styles.commandBarButtonRoot, rootDisabled: styles.commandBarButtonRoot, rootChecked: styles.commandBarButtonRootChecked, iconChecked: styles.commandBarButtonRootChecked },
                onClick: () => {
                    localStorage.setItem(VIEW_KEY, !galleryView ? "GalleryView" : "ListView");
                    setGalleryView(!galleryView);
                }
            }
        );
        return retVal;
    };

    const getFiles = (allFiles: ICourseWorkFlat[]) => {
        if(!showHiddenFiles)
        {
            allFiles = allFiles.filter(f => !f.IsCourseWorkHidden);
        }
        allFiles.forEach(f => {
            f.MatchesCurrentSearch = f.FileName.toLowerCase().includes((searchText || "").toLowerCase()) || f.Student.toLowerCase().includes((searchText || "").toLowerCase());
        })
        return allFiles;
    }

    const currentUserEmail = authContext.Result?.KeplerAuthStatus ? authContext.Result?.KeplerAuthStatus.account.username : authContext.Result?.CanvasAuthStatus?.userEmail;

    return <div className="coursework-wrapper">
        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
        {assignmentContext.SelectedAssignment && <h2 style={{ fontWeight: "normal" }}>{assignmentContext.SelectedAssignment.Name}</h2>}
        {assignmentContext.SelectedAssignment &&
            <CourseWorkProvider assignment={assignmentContext.SelectedAssignment} resetFlag={resetFlag}>
                <CourseWorksContext.Consumer>{(value: IApiContext<ICourseWorkFlat[]> & { AccreditorStudentsAssigned?: string[] }) => <div>
                    {value.Error && <MessageBar messageBarType={MessageBarType.error}>{value.Error}</MessageBar>}
                    {value.Result && maxFileSizeBeforeStream &&
                        <FileUploadControl allowMultiple={true}
                            shouldOpenDialog={isFileUploadDialogOpen}
                            postUrlPath={`https://${getServerHost()}/api/blobStorage/uploadCourseWorkDocument`}
                            isReadOnly={!(currentUserIsStudentInCourseAndCourseIsActive || currentUserIsTeacherInCourseAndCourseIsActive)}
                            params={{ assignment: JSON.stringify(assignmentContext.SelectedAssignment), user: authContext.Result?.UserInfo?.DisplayName, userEmail: currentUserEmail }}
                            onSuccess={(file) => {
                                setResetFlag(!resetFlag);
                            }}
                        >
                            <CommandBar styles={{ root: styles.commandBarRoot }} items={_getCommandBarItems(value.Result)} farItems={_getCommandBarFarItems()} />
                            {(galleryView && !value.Pending) ? <GalleryView props={{
                                shouldGetDataFromCanvasAPIs: shouldGetDataFromCanvasAPIs,
                                allowedTags: allowedTags!,
                                studentsAssignedToAccreditor: value.AccreditorStudentsAssigned,
                                assignmentTagMapping: assignmentTagMapping,
                                files: getFiles(value.Result),
                                selectedMyCoursework: selectedMyCoursework,
                                currentStudent: authContext.Result?.UserInfo?.DisplayName,
                                authContext: authContext.Result,
                                accreditationRulesContext: accreditorRulesContext,
                                accreditorHiddenCourseWorks: accreditorHiddenCourseWorks,
                                semseter: { Semester: assignmentContext.SelectedAssignment!.Course.Semester, Year: assignmentContext.SelectedAssignment!.Course.Year },
                                assignment: assignmentContext.SelectedAssignment!,
                                studentsInCourse: students || [],
                                showHiddenCourseWorks: false,
                                maxFileSizeBeforeStream: maxFileSizeBeforeStream
                            }} /> :
                                <>
                                    {!value.Pending && <CourseWorkListView props={{
                                        shouldGetDataFromCanvasAPIs: shouldGetDataFromCanvasAPIs,
                                        allowedTags: allowedTags!,
                                        studentsAssignedToAccreditor: value.AccreditorStudentsAssigned,
                                        assignmentTagMapping: assignmentTagMapping,
                                        files: getFiles(value.Result),
                                        selectedMyCoursework: selectedMyCoursework,
                                        currentStudent: authContext.Result?.UserInfo?.DisplayName,
                                        authContext: authContext.Result,
                                        accreditationRulesContext: accreditorRulesContext,
                                        accreditorHiddenCourseWorks: accreditorHiddenCourseWorks,
                                        semseter: { Semester: assignmentContext.SelectedAssignment!.Course.Semester, Year: assignmentContext.SelectedAssignment!.Course.Year },
                                        assignment: assignmentContext.SelectedAssignment!,
                                        studentsInCourse: students || [],
                                        showHiddenCourseWorks: showHiddenFiles,
                                        maxFileSizeBeforeStream: maxFileSizeBeforeStream
                                    }} />}</>}
                        </ FileUploadControl>}
                    {isTagConfigurationPanelOpen && <TagConfigurationPanel assignment={assignmentContext.SelectedAssignment!} onClose={() => setIsTagConfigurationPanelOpen(false)} onRefresh={function (): void {
                        throw new Error('Function not implemented.');
                    }} />}
                    {value.Pending && galleryView && <ProgressIndicator />}
                    {value.Pending && !galleryView && <ShimmeredDetailsList layoutMode={DetailsListLayoutMode.fixedColumns} enableShimmer={true} columns={shimmerColumns} items={fakeCourseWorks} />}
                </div>}</CourseWorksContext.Consumer>
            </CourseWorkProvider>
        }
        {!assignmentContext.SelectedAssignment && <Stack style={{ display: "block" }}>
            <Stack horizontal style={{ paddingTop: 10 }}>
                <Stack.Item align='start' grow>Click an assignment to view the coursework</Stack.Item>
                {currentUserIsTeacherInCourseAndCourseIsActive && <Stack.Item align="end"><div id='file-embed-wrapper'>
                    <input ref={backgroundImageInputRef} type="file" id="fileElem" accept=".png" style={{
                        position: "absolute",
                        height: 1,
                        width: 1,
                        overflow: "hidden",
                        clip: "rect(1px, 1px, 1px, 1px)"
                    }} onChange={(_ev) => {
                        const files = _ev?.target?.files;
                        if (files) {
                            let file = files[0];
                            const imagePath = `${course.BlobFolderName}/course_landing.png`;
                            const fileUploadUrl = `/api/blobStorage/uploadCourseBackgroundImage?blobPath=${encodeURIComponent(imagePath)}`;
                            let formData = new FormData();
                            formData.append("files", file);
                            setUploadingLandingPage(true);
                            sendApiRequest(fileUploadUrl, "POST", formData, undefined, true).then(res => {
                                const url = `/api/blobStorage/getBlobAsBase64FromPath?path=${encodeURIComponent(imagePath)}`;
                                sendApiRequest(url, "GET").then(res => {
                                    setLandingPageImage(res);
                                    setUploadingLandingPage(false);
                                }).catch(err => {
                                    console.log(err);
                                    setUploadingLandingPage(false);
                                });
                            }).catch(err => {
                                console.log(err);
                                setUploadingLandingPage(false);
                            });
                        }
                    }} />
                    <Stack horizontal horizontalAlign='end'><Link id="fileSelect" onClick={() => {
                        backgroundImageInputRef.current?.click();
                    }}>Click here to update the course landing page image (must be a .png)</Link></Stack>
                </div></Stack.Item>
                }
            </Stack>
            {landingPageImage && currentUserIsTeacherInCourseAndCourseIsActive &&
                <Stack horizontal horizontalAlign='end'><Link onClick={() => {
                    const imagePath = `${course.BlobFolderName}/course_landing.png`;
                    const fileRemoveUrl = `/api/blobStorage/deleteBlob?blobPath=${encodeURIComponent(imagePath)}`;
                    setUploadingLandingPage(true);
                    sendApiRequest(fileRemoveUrl, "DELETE").then(res => {
                        setLandingPageImage(undefined);
                    }).catch(err => {
                        console.log(err);
                    }).finally(() => {
                        setUploadingLandingPage(false);
                    })
                }}>Remove course landing page image</Link></Stack>}
            {uploadingLandingPage && <Spinner />}
            <br />
            {landingPageImage && <img src={`data:image/png;base64,${landingPageImage}`} alt="Placeholder for coursework" style={{ objectFit: "contain", height: "calc(100vh - 210px)", maxWidth: "calc(100vw - 400px)" }} />}
        </Stack>}
    </div>;
};

export default Coursework;